import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import TamaguiInput from "../components/shared/tamagui-input";
import Typography from "../components/shared/typography";
import { useAuth } from "../hooks/AuthContext";
import useUserDetails from "../hooks/useUserDetails";
import { useLoadContacts } from "../hooks/useLoadContacts";

const ClaimAccountPage = () => {
  const supabase = useSupabaseClient();
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const { userDetails } = useUserDetails();
  const { contacts } = useLoadContacts();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [checkedToken, setCheckedToken] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const type = params.get("type");

    if (accessToken && refreshToken && type) {
      supabase.auth
        .setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error }) => {
          if (error) {
            setError(error.message);
          }
          setCheckedToken(true);
        });
    } else {
      setError("Invalid or missing claim token");
      setCheckedToken(true);
    }
  }, [supabase]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setError(null);
    await updatePassword(password);
    navigate("/login");
  };

  if (!checkedToken) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <Typography variant="body1">Verifying token...</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <Typography variant="body1" className="text-red-500">
          {error}
        </Typography>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="w-full max-w-md mx-auto" variant="cream" elevation={2}>
        <Card.Header variant="cream" className="text-center">
          <Typography variant="h3" className="text-primary">
            Claim Your Account
          </Typography>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="space-y-1 text-center">
            <Typography variant="body1" className="text-terracotta">
              {userDetails?.first_name} {userDetails?.last_name}
            </Typography>
            {contacts.length > 0 && (
              <Typography variant="body2" className="text-terracotta-900">
                Contacts:{" "}
                {contacts
                  .slice(0, 3)
                  .map(
                    (c) => `${c.related_user.first_name} ${c.related_user.last_name}`
                  )
                  .join(", ")}
                ...
              </Typography>
            )}
            <Typography variant="caption" className="text-terracotta-900">
              {window.location.pathname}
            </Typography>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TamaguiInput
              id="new-password"
              label="New Password"
              value={password}
              onChangeText={setPassword}
              fullWidth
              inputType="password"
            />
            <TamaguiInput
              id="confirm-password"
              label="Confirm Password"
              value={confirm}
              onChangeText={setConfirm}
              fullWidth
              inputType="password"
            />
            <SubmitButton onSubmit={handleSubmit} isDisabled={!password || !confirm}>
              Set Password
            </SubmitButton>
          </form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ClaimAccountPage;
